import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import Hud from "./HudOverlay";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70, duration: 1.2 });
  else el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const ITEMS = [
  { value: "LATAM · Europe · UAE", label: "Regions served" },
  { value: 9, suffix: "+", label: "Client launches" },
  { value: 10, suffix: "+", label: "Platform & provider partners" },
  { value: "Client testimonials →", label: "Verified feedback", action: "about" },
];

const Counter = ({ value, suffix, start }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min((t - t0) / 1400, 1);
      setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, value]);
  return <span style={{ fontVariantNumeric: "tabular-nums" }}>{n}{suffix}</span>;
};

/* Compact professional trust strip under the hero */
const TrustStrip = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  return (
  <section className="relative py-4" data-testid="trust-strip">
    <div className="relative max-w-7xl mx-auto px-5 sm:px-10">
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="lux-card relative overflow-hidden rounded-2xl px-6 sm:px-10 py-6 sm:py-7"
      >
        <Hud />
        <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />
        <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-y-6 gap-x-4">
          {ITEMS.map((it, i) => {
            const inner = (
              <>
                <div className="font-display text-lg sm:text-xl gold-text leading-tight">
                  {typeof it.value === "number" ? <Counter value={it.value} suffix={it.suffix} start={inView} /> : it.value}
                </div>
                <div className="mt-2 text-[10px] tracking-[0.35em] uppercase text-zinc-400">
                  {it.label}
                </div>
              </>
            );
            const base = "relative pl-4 border-l border-purple-500/30 text-left block w-full";
            return (
              <motion.div
                key={it.label}
                initial={{ opacity: 0, x: i % 2 === 0 ? -24 : 24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                {it.action ? (
                  <button
                    type="button"
                    onClick={() => scrollTo(it.action)}
                    className={`${base} hover:opacity-90 transition-opacity cursor-pointer`}
                    data-testid="trust-testimonials-link"
                  >
                    {inner}
                  </button>
                ) : (
                  <div className={base} data-testid={`trust-${it.label.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
                    {inner}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </div>
  </section>
  );
};

export default TrustStrip;
